import { Injectable } from '@angular/core';
import { jsPDF } from "jspdf";

@Injectable()
export abstract class PdfExportCertificatesGeBaseService {
  protected doc: jsPDF;

  protected currentYValue = 0;
  protected boldLeftOnToggle = false;
  protected boxTopBottomPaddingPage2 = 10;

  // A4 Size - 210 x 297 mm / 8.27 x 11.60 inches / 595 x 842 pt
  protected readonly a4Width = 595;
  protected readonly a4Height = 842;

  protected readonly logoUrl = './assets/img/ventus-certificates/ge_logo.png';
  protected readonly signatureUrlGarySmith = './assets/img/ventus-certificates/ge_cert_signature_gary_smith.png';
  protected readonly signatureUrlRauriMaguire = './assets/img/ventus-certificates/ge_cert_signature_rauri_maguire.png';

  protected readonly fontSizePage1Header = 20;
  protected readonly fontSizePage1SubHeader = 10;
  protected readonly fontSizePage1Body = 8;
  protected readonly fontSizePage1SmallText = 6;

  protected readonly boxSizePage1 = 16;

  protected readonly fontSizePage2Header = 16;
  protected readonly fontSizePage2SubHeader = 8;
  protected readonly fontSizePage2Body = 8;
  protected readonly fontSizePage2SmallText = 6;

  protected abstract needsManagementRepresentitiveSignature: boolean;
  protected abstract getAuthorisingEngineerSignatureName(): string;
  protected abstract getAuthorisingEngineerSignatureUrl(): string;
  protected abstract getManagementRepresentitiveSignatureUrl(): string;
  protected abstract getManagementRepresentitiveSignatureName(): string;
  protected abstract fillInApprovedCodes(): void;

  public getCertificateFile(certificateDetails, currentDate, expiryDate): Blob {
    return this.createCertificate(certificateDetails.workerName, certificateDetails.company, currentDate, expiryDate);
  }

  public downloadCertificate(certificateDetails, currentDate, expiryDate): any {
    this.createCertificate(certificateDetails.workerName, certificateDetails.company, currentDate, expiryDate);

    const fileName = `${certificateDetails.siteName} - ${certificateDetails.inductionName} Certificate.pdf`;
    this.doc.save(fileName);
  }

  protected createCertificate(workerName, company, currentDate, expiryDate): Blob {
    this.doc = new jsPDF('p', 'pt', 'a4', true);

    this.createPage1(workerName, company, currentDate, expiryDate);

    this.doc.addPage();
    this.doc.setPage(2);
    this.createPage2();

    return this.doc.output('blob');
  }

  protected createPage1(workerName, company, currentDate, expiryDate) {

    this.doc.setDrawColor(56, 56, 56);

    // --- Header --- //
    this.doc.addImage(this.logoUrl, 'PNG', 38, 15, 77, 0); // 0 for auto height

    this.doc.setFontSize(this.fontSizePage1Header);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('CERTIFICATE OF AUTHORISATION', 154, 48);
    this.doc.setFont('helvetica', 'normal');

    this.doc.setFontSize(this.fontSizePage1SubHeader);

    // --- Name and Company Box --- //
    this.doc.rect(30, 76, this.a4Width - 60, 21);
    this.doc.text('NAME:', 35, 90);
    this.doc.line(73, 76, 73, 97);

    this.doc.setFont('helvetica', 'bold');
    this.currentYValue = 85;
    const widthOfName = this.doc.getTextWidth(workerName);
    if (widthOfName > 185) {
      // use smaller font
      this.splitTextOverManyLinesAtCurrentY(workerName, 188, 75, this.fontSizePage1SmallText + 3, this.fontSizePage1SmallText);
    } else {
      this.doc.text(workerName, (258 + 73 - widthOfName) / 2, 90);
    }
    this.doc.setFont('helvetica', 'normal');

    this.doc.line(258, 76, 258, 97);
    this.doc.text('COMPANY/DEPARTMENT:', 262, 90);
    this.doc.line(390, 76, 390, 97);

    this.doc.setFont('helvetica', 'bold');
    this.currentYValue = 85;

    const widthOfCompanyName = this.doc.getTextWidth(company);
    if (widthOfCompanyName > 185) {
      // use smaller font
      this.splitTextOverManyLinesAtCurrentY(company, 185, 391, this.fontSizePage1SmallText + 3, this.fontSizePage1SmallText);
    } else {
      this.doc.text(company, (this.a4Width - 30 + 390 - widthOfCompanyName) / 2, 90);
    }

    // --- text paragraph --- //
    this.doc.setFontSize(this.fontSizePage1Body);
    this.currentYValue = 107;
    let paragraph = 'The above named person is authorised in the following categories for duties under the GE Electrical & Mechanical Safety Rules. Category details are outlined on the reverse side of the certificate.'
    this.splitTextOverManyLinesAtCurrentY(paragraph, this.a4Width - 60, 33, 11, this.fontSizePage1Body);

    // --- Group Tables --- //

    this.currentYValue = 140;

    this.addPage1BoxHeader('GROUP A - SENIOR AUTHORISED PERSON', );

    this.addPage1Box('A1_');
    this.addPage1Box('A2_');
    this.addPage1Box('A3_');
    this.addPage1Box('A4_');
    this.addPage1Box('A5_');
    this.addPage1Box('A6X');
    this.addPage1Box('A7X');

    this.currentYValue += 15;

    this.addPage1BoxHeader('GROUP B - AUTHORISED PERSON');
    this.addPage1Box('B1_');
    this.addPage1Box('B2X');

    this.currentYValue += 15;

    this.addPage1BoxHeader('GROUP C - CONTROL PERSON');
    this.addPage1Box('C1X');
    this.addPage1Box('C2X');
    this.addPage1Box('C3A');

    this.currentYValue += 15;

    this.addPage1BoxHeader('GROUP D - COMPETENT PERSON');
    this.addPage1Box('D1_');
    this.addPage1Box('D2_');
    this.addPage1Box('D3_');
    this.addPage1Box('D4_');
    this.addPage1Box('D5_');
    this.addPage1Box('D6_');
    this.addPage1Box('D7X');

    this.currentYValue += 15;

    this.addPage1BoxHeader('GROUP E - PERSON');
    this.addPage1Box('E1_');
    this.addPage1Box('E2_');

    this.currentYValue += 15;

    this.addPage1BoxHeader('GROUP F - SELECTED PERSON');
    this.addPage1Box('F1_');

    this.currentYValue += 15;

    this.addPage1BoxHeader('GROUP G - PERSON IN TRAINING');
    this.addPage1Box('G1_');

    this.currentYValue += 15;

    // --- text paragraph --- //
    let paragraph2 = 'An Authorisation Committee has examined the above named Candidate and is unanimously satisfied that he has the necessary technical knowledge and experience to be authorised under the GE Electrical & Mechanical Safety Rules and **Approved** Procedures in accordance with the Authorisation Categories specified above.';
    this.splitTextOverManyLinesAtCurrentY(paragraph2, this.a4Width - 60, 33, 11, this.fontSizePage1Body);

    // --- Signature table --- //
    const cellWidth = (this.a4Width - 90) / 4;
    const cellHeight = 21;
    // 3 vertical rectangles
    this.doc.rect(45 + (cellWidth * 1), this.currentYValue, cellWidth, cellHeight * 3);
    this.doc.rect(45 + (cellWidth * 2), this.currentYValue, cellWidth, cellHeight * 3);
    this.doc.rect(45 + (cellWidth * 3), this.currentYValue, cellWidth, cellHeight * 3);

    // first row text
    this.doc.text('Name', 45 + (cellWidth * 3 / 2) - (this.doc.getTextWidth('Name') / 2), this.currentYValue + 13);
    this.doc.text('Signature', 45 + (cellWidth * 5 / 2) - (this.doc.getTextWidth('Signature') / 2), this.currentYValue + 13);
    this.doc.text('Date', 45 + (cellWidth * 7 / 2) - (this.doc.getTextWidth('Date') / 2), this.currentYValue + 13);

    // 2 horizontal rectangles
    this.currentYValue += cellHeight;
    this.doc.rect(45, this.currentYValue, cellWidth * 4, cellHeight);
    this.doc.text('Authorising Engineer:', 45 + (cellWidth * 1 / 2) - (this.doc.getTextWidth('Authorising Engineer:') / 2), this.currentYValue + 13);
    this.doc.text(this.getAuthorisingEngineerSignatureName(), 45 + (cellWidth * 3 / 2) - (this.doc.getTextWidth(this.getAuthorisingEngineerSignatureName()) / 2), this.currentYValue + 13);
    this.doc.addImage(this.getAuthorisingEngineerSignatureUrl(), 'PNG', 45 + (cellWidth * 5 / 2) - (120 / 2), this.currentYValue + 1, 120, 0); // 0 for auto height
    this.doc.text(currentDate, 45 + (cellWidth * 7 / 2) - (this.doc.getTextWidth(currentDate) / 2), this.currentYValue + 13);
    this.currentYValue += cellHeight;

    this.doc.rect(45, this.currentYValue, cellWidth * 4, cellHeight);
    this.doc.text('Management Representative:', 45 + (cellWidth * 1 / 2) - (this.doc.getTextWidth('Management Representative:') / 2), this.currentYValue + 13);

    if (this.needsManagementRepresentitiveSignature) {
      this.doc.text(this.getManagementRepresentitiveSignatureName(), 45 + (cellWidth * 3 / 2) - (this.doc.getTextWidth(this.getManagementRepresentitiveSignatureName()) / 2), this.currentYValue + 13);
      this.doc.addImage(this.getManagementRepresentitiveSignatureUrl(), 'PNG', 45 + (cellWidth * 5 / 2) - (120 / 2), this.currentYValue + 1, 120, 0); // 0 for auto height
      this.doc.text(currentDate, 45 + (cellWidth * 7 / 2) - (this.doc.getTextWidth(currentDate) / 2), this.currentYValue + 13);
    }

    // --- Expirary Date --- //
    this.currentYValue += 55;

    this.doc.text('Date of Expiration:', 139, this.currentYValue);
    this.doc.line(208, this.currentYValue + 3, 457, this.currentYValue + 3);
    this.doc.text(expiryDate, (this.a4Width / 2), this.currentYValue);

    // --- Document Code --- //
    this.doc.rect(442, 800, 122, 16);
    this.doc.text('EHS-Ren-P&S-E7.3.2', 483, 810);

    this.fillInApprovedCodes();
  }

  protected createPage2() {
    // --- Main Header --- //
    this.doc.setFontSize(this.fontSizePage2Header);
    const header = 'AUTHORISATION CATEGORIES';
    let width = this.doc.getTextWidth(header);

    this.doc.setFont('helvetica', 'bold');
    this.doc.text(header, (this.a4Width - width) / 2, 30);
    this.doc.setFont('helvetica', 'normal');

    // --- Codes Glossary --- //
    this.currentYValue = 33;

    this.boxTopBottomPaddingPage2 = 10;

    this.page2SubHeader('GROUP A - SENIOR AUTHORISED PERSON');
    this.page2CodeBlock(
      'A1_',
      [
        'Prepare, issue, transfer, suspend and cancel a **Permit To Work**.',
        'Decide what category of **Person** shall provide **Personal Supervision** when it is required.',
      ]
    );
    this.page2CodeBlock(
      'A2_',
      [
        'Prepare, issue, transfer and cancel a **Sanction For Test**.',
      ]
    );
    this.page2CodeBlock(
      'A3_',
      [
        'Prepare, issue, transfer, suspend and cancel a **Limitation Of Access** when work or testing is to be done outside **Safety Distances** but in close proximity to the limits of **Safety Distances** to **HV Equipment** and to decide whether to issue a **Limitation Of Access** in such circumstances.',
      ]
    );
    this.page2CodeBlock(
      'A4_',
      [
        'Prepare, issue, transfer suspend and cancel a **Limitation Of Access** when **Safety Distances** to **HV Equipment** do not have to be considered. To achieve **Safety from the System** by limiting the work or testing or the work area of to decide whether a **Limitation Of Access** is required to confirm oral instructions in such circumstances.',
      ]
    );
    this.page2CodeBlock(
      'A5_',
      [
        'Prepare, issue, transfer, suspend and cancel a **Permit To Work** for work or testing on **LV Equipment**.',
        'Decide whether work or testing on **LV Equipment** is to be carried out with the **Equipment Isolated**, or with the **Equipment Live** and in accordance with the relevant Risk Assessment and Method Statement, and to decide whether such work or testing is to be carried out under **Approved** Local Procedures, Oral Instructions, a **Permit to Work** or **Personal Supervision**.',
        'Decide what category of Person shall provide **Personal Supervision** when it is required.',
      ]
    );
    this.page2CodeBlock(
      'A6X',
      [
        'Carry out duties allocated to **Senior Authorised Persons** as specified in **Approved Procedures**.',
      ]
    );
    this.page2CodeBlock(
      'A7X',
      [
        'Carry out duties allocated to an **Authorising Engineer** as specified in **Approved Procedures**.',
      ]
    );


    this.page2SubHeader('GROUP B - AUTHORISED PERSON');
    this.page2CodeBlock(
      'B1_',
      [
        'Apply and remove safety precautions to the instructions of **Control Persons**.',
      ]
    );
    this.page2CodeBlock(
      'B2X',
      [
        'Carry out duties allocated to **Authorised Persons** as specified in **Approved Procedures**.',
      ]
    );

    this.page2SubHeader('GROUP C - CONTROL PERSON');
    this.page2CodeBlock(
      'C1X',
      [
        'Carry out duties of a **Control Person** at a System Control Centre on specified **Equipment**.',
      ]
    );
    this.page2CodeBlock(
      'C2X',
      [
        'Carry out duties of a **Control Person** on that part of the **HV System** transferred to them under the relevant Delegated Control **Approved** Procedure.',
      ]
    );
    this.page2CodeBlock(
      'C3A',
      [
        'Carry out duties of a **Control Person** on **LV Equipment**.',
      ]
    );

    this.page2SubHeader('GROUP D - COMPETENT PERSON');
    this.page2CodeBlock(
      'D1_',
      [
        'Fit and remove Portable **Primary Earths** under the **Personal Supervision** of a **Senior Authorised Person**.',
      ]
    );
    this.page2CodeBlock(
      'D2_',
      [
        'Receive and clear a **Limitation Of Access**.',
      ]
    );
    this.page2CodeBlock(
      'D3_',
      [
        'Receive and clear a **Permit To Work**.',
      ]
    );
    this.page2CodeBlock(
      'D4_',
      [
        'Work on **Live LV Equipment** in accordance with an **Approved** Risk Assessment and Method Statement.',
      ]
    );
    this.page2CodeBlock(
      'D5_',
      [
        'Receive and clear a **Sanction For Test**.',
      ]
    );
    this.page2CodeBlock(
      'D6_',
      [
        'Fit and remove **Drain Earths**.',
      ]
    );
    this.page2CodeBlock(
      'D7X',
      [
        'Carry out duties allocated to **Competent Persons** as specified in **Approved** Procedures.',
      ]
    );

    this.page2SubHeader('GROUP E - PERSON');
    this.page2CodeBlock(
      'E1_',
      [
        'Gain access to Substations containing **HV Equipment** to perform duties in a manner such that **Safety Distances** will not be infringed and that **System** derived hazards will not cause danger.',
      ]
    );
    this.page2CodeBlock(
      'E2_',
      [
        'Work as a member of a **Working Party** on the **System** in accordance with the Ventus Energy Electrical & Mechanical Safety Rules and **Approved** Procedures.',
      ]
    );

    this.page2SubHeader('GROUP F - SELECTED PERSON');
    this.page2CodeBlock(
      'F1_',
      [
        'Make reports and recommendations to overcome hazards which may prevent work or testing being performed safely on **Equipment** which has otherwise been made safe.',
      ]
    );

    this.page2SubHeader('GROUP G - PERSON IN TRAINING');
    this.page2CodeBlock(
      'G1_',
      [
        'Carry out duties as directed by, and under the **Personal Supervision** of a **Person** authorised to carry out such duties.',
      ]
    );

    // --- text paragraph --- //
    // extra line break
    this.currentYValue += 24;

    this.splitTextOverManyLinesAtCurrentY('The 3rd character of the Authorisation Category is taken from the following list of subscripts \'A\' to \'G\'. Where the 3rd character is \'X\', this indicates that specific duties are detailed in this Certificate Of Authorisation', this.a4Width - 60, 33, 11, this.fontSizePage1Body);

    this.boxTopBottomPaddingPage2 = 5;

    // --- Voltage Glossary --- //
    this.currentYValue -= 8;

    this.page2CodeBlock('A', ['**LV Equipment**']);
    this.page2CodeBlock('B', ['**Equipment** nominally operating up to 33Kv']);
    this.page2CodeBlock('C', ['**Equipment** nominally operating up to 66kV']);
    this.page2CodeBlock('D', ['**Equipment** nominally operating up to 132Kv']);
    this.page2CodeBlock('E', ['**Equipment** nominally operating up to 275Kv']);
    this.page2CodeBlock('F', ['**Equipment** nominally operating up to 400kV']);
    this.page2CodeBlock('X', ['Specified in text.']);
  }

  protected addPage1BoxHeader(header: string) {
    const smallText = 'Restrictions/Limitations/Specific Text';

    this.doc.text(header, 30, this.currentYValue);

    this.doc.setFontSize(this.fontSizePage1SmallText);
    this.doc.setFont('Helvetica', 'italic');
    const width = this.doc.getTextWidth(smallText);
    this.doc.text(smallText, (this.a4Width - 30 - width), this.currentYValue + 2);

    this.doc.setFontSize(this.fontSizePage1Body);
    this.doc.setFont('Helvetica', 'normal');

    this.currentYValue += 5;
  }

  protected addPage1Box(text: string) {
    this.doc.rect(30, this.currentYValue, this.a4Width - 60, this.boxSizePage1);
    this.doc.text(text, 45, this.currentYValue + 10);
    this.doc.line(73, this.currentYValue, 73, this.currentYValue + this.boxSizePage1);

    this.currentYValue += this.boxSizePage1;
  }

  protected splitTextOverManyLinesAtCurrentY(paragraph: string, textWidth: number, xCoordinate: number, lineHeight: number, textSize: number) {
    const textArray = this.doc.splitTextToSize(paragraph, textWidth);
    textArray.forEach(textPart => {
      this.textWithBoldInline(textPart.trim(), xCoordinate, this.currentYValue, textSize);
      this.currentYValue += lineHeight;
    });

    return textArray.length;
  }

  protected textWithBoldInline(inputValue: string, xCoordinate: number, yCoordinate: number, textSize: number) {
    const arrayOfNormalAndBoldText = inputValue.split('**');

    // did previous line end in unfinished bold section?
    if (!this.boldLeftOnToggle) {
      arrayOfNormalAndBoldText.map((text, i) => {
        this.doc.setFont('helvetica', 'bold');
        // every even item is a normal font weight item

        if (i % 2 === 0) {
          this.doc.setFont('helvetica', 'normal');
          this.boldLeftOnToggle = false;
        } else {
          this.boldLeftOnToggle = true;
        }

        this.doc.text(text, xCoordinate, yCoordinate);
        xCoordinate = xCoordinate + 1 + (this.doc.getStringUnitWidth(text) * textSize);
      });
    } else {
      arrayOfNormalAndBoldText.map((text, i) => {

        this.doc.setFont('helvetica', 'bold');
        // every even item is a normal font weight item

        if (i % 2 !== 0) {
          this.doc.setFont('helvetica', 'normal');
          this.boldLeftOnToggle = false;
        } else {
          this.boldLeftOnToggle = true;
        }

        this.doc.text(text, xCoordinate, yCoordinate);
        xCoordinate = xCoordinate + 1 + (this.doc.getStringUnitWidth(text) * textSize);
      });
    }
  }

  protected page2SubHeader(subHeader: string) {
    this.currentYValue += 16;

    this.doc.setFontSize(this.fontSizePage2SubHeader);
    this.doc.text(subHeader, 30, this.currentYValue);

    this.currentYValue += 5;
  }

  protected page2CodeBlock(code: string, lines: string[]) {
    // keep box y coordinate, add on for padding.
    const startingYCoordinate = this.currentYValue;
    this.currentYValue += 10;

    let numberOfLines = 0;

    // right column: explanation
    this.doc.setFontSize(this.fontSizePage1SmallText);

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      numberOfLines += this.splitTextOverManyLinesAtCurrentY(line, this.a4Width - 120, 80, 8, this.fontSizePage2SmallText);
    }
    const heightOfBox = this.boxTopBottomPaddingPage2 + (8 * numberOfLines);

    // left column: Code
    this.doc.setFontSize(this.fontSizePage2Body);
    if (numberOfLines === 1) {
      this.doc.text(code, 45, startingYCoordinate + 10);
    } else {
      this.doc.text(code, 45, startingYCoordinate + (heightOfBox / 2));
    }

    // draw box around text
    this.doc.rect(30, startingYCoordinate, this.a4Width - 60, heightOfBox);
    this.doc.line(73, startingYCoordinate, 73, startingYCoordinate + heightOfBox);

    // update y
    this.currentYValue = startingYCoordinate + heightOfBox;
  }
}
